import { type FC, type ReactNode, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedDescriptionAbout } from '../../PdpSharedDescriptions/PdpSharedDescriptionAbout.tsx';
import { PdpRespDetails } from '../../PdpRespDetails/PdpRespDetails.tsx';
import PdpRespSimilarSoldItemsLoading from '../../PdpRespSimilarSoldItems/PdpRespSimilarSoldItemsLoading.tsx';
import { useSimilarSoldItemsTracking } from '../../hooks/useSimilarSoldItemsTracking.ts';
import { useShouldShowSimilarSoldItems } from '../../hooks/useShouldShowSimilarSoldItems.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import { type PdpRespSharedAboutItemPanel_viewer$key } from './__generated__/PdpRespSharedAboutItemPanel_viewer.graphql.ts';
import { type PdpRespSharedAboutItemPanel_item$key } from './__generated__/PdpRespSharedAboutItemPanel_item.graphql.ts';
import { type PdpRespSharedAboutItemPanel_user$key } from './__generated__/PdpRespSharedAboutItemPanel_user.graphql.ts';

const PdpRespSimilarSoldItems = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSimilarSoldItems" */ '../../PdpRespSimilarSoldItems/PdpRespSimilarSoldItems.tsx'
        )
);

type Props = {
    showHeader?: boolean;
    viewer: PdpRespSharedAboutItemPanel_viewer$key;
    item: PdpRespSharedAboutItemPanel_item$key;
    user: PdpRespSharedAboutItemPanel_user$key | null | undefined;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    afterDisplay: boolean;
    dataTn?: string;
    children?: ReactNode;
    showThirdCol: boolean;
    isAvailableItem?: boolean;
};

const viewerFragment = graphql`
    fragment PdpRespSharedAboutItemPanel_viewer on Viewer {
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItems_viewer @include(if: $afterDisplay)
        }
        ...useShouldShowSimilarSoldItems_viewer
        ...useSimilarSoldItemsTracking_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespSharedAboutItemPanel_item on Item {
        ...PdpSharedDescriptionAbout_item
        ...PdpRespDetails_item
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItemsLoading_item @include(if: $afterDisplay)
            ...PdpRespSimilarSoldItems_item @include(if: $afterDisplay)
        }
        ...useShouldShowSimilarSoldItems_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedAboutItemPanel_user on User {
        ...PdpRespDetails_user
        ... @include(if: $isPdpSimilarSoldItemsModuleVariant) {
            ...PdpRespSimilarSoldItems_user @include(if: $afterDisplay)
        }
    }
`;

export const PdpRespSharedAboutItemPanel: FC<Props> = ({
    showHeader,
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    currency,
    selectedSkuId,
    showExpandingArea,
    dataTn,
    afterDisplay,
    children,
    showThirdCol,
    isAvailableItem,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const {
        shouldShowSimilarSoldItemsModule,
        areEligibleConditionsMet,
        isBelowMinPriceEstimate,
        isBelowOrEqualToMaxPriceEstimate,
    } = useShouldShowSimilarSoldItems({
        viewer: viewer || null,
        item: item || null,
        currency,
        afterDisplay,
    });

    const { trackSimilarSoldItemsModuleVisible } = useSimilarSoldItemsTracking({ viewer });

    const { isTablet } = useServerVarsContext();

    // For details collapsed height = [# of lines to show] * ([line-height] + [margin height]) - [margin of container]
    const singleColumnDetailsHeight = 9 * (24 + 9) - 9;

    return (
        <>
            <GridRow dataTn={dataTn}>
                <GridCol
                    desktop={showThirdCol ? 4 : 5}
                    tabletPortrait={showThirdCol ? 6 : 12}
                    tabletLandscape={showThirdCol ? 6 : undefined}
                >
                    <div className={dibsCss.mbSmall}>
                        <PdpSharedDescriptionAbout
                            item={item}
                            headerClass={
                                showHeader
                                    ? classnames(
                                          dibsCss.sassyFontBodyMediumHeavy,
                                          dibsCss.m0,
                                          dibsCss.mrXsmall
                                      )
                                    : dibsCss.hidden
                            }
                            title={
                                <FormattedMessage
                                    id="PdpRespSharedAboutItemPanel.aboutHeader"
                                    defaultMessage="About the Item"
                                />
                            }
                            // line height * number of lines
                            collapsedHeight={24 * 10}
                            showExpandingArea={showExpandingArea}
                        />
                    </div>
                </GridCol>
                {showThirdCol && isTablet && children}
                <GridCol
                    desktop={showThirdCol ? 4 : 6}
                    desktopOptions={
                        !showThirdCol
                            ? {
                                  offset: 1,
                              }
                            : undefined
                    }
                    tabletPortrait={12}
                    tabletLandscape={12}
                >
                    {/* Details */}
                    <PdpRespDetails
                        variant={
                            showThirdCol && !isTablet ? 'pdpRespTabsVariant' : 'pdpRedesignVariant'
                        }
                        showHeader={false}
                        afterDisplay={afterDisplay}
                        currency={currency}
                        item={item}
                        selectedSkuId={selectedSkuId}
                        user={user}
                        collapsedHeight={singleColumnDetailsHeight}
                        showTopBorder={isTablet}
                    />
                </GridCol>
                {showThirdCol && !isTablet && children}
            </GridRow>
            {/* pricingGuidance data is available only afterDisplay, so areEligibleConditionsMet, isBelowMinPriceEstimate & isBelowOrEqualToMaxPriceEstimate won't be set before */}
            {afterDisplay && (
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible && isAvailableItem && areEligibleConditionsMet) {
                            trackSimilarSoldItemsModuleVisible({
                                isBelowMinPriceEstimate,
                                isBelowOrEqualToMaxPriceEstimate,
                            });
                        }
                    }}
                />
            )}
            {isAvailableItem && shouldShowSimilarSoldItemsModule && (
                <Suspense fallback={<PdpRespSimilarSoldItemsLoading item={item} />}>
                    <PdpRespSimilarSoldItems
                        viewer={viewer}
                        item={item}
                        user={user}
                        currency={currency}
                    />
                </Suspense>
            )}
        </>
    );
};
